import { mergeAttributes, Node } from "@tiptap/core"
import type { Editor } from "@tiptap/react"
import { ReactNodeViewRenderer } from "@tiptap/react"
import type { Node as ProseMirrorNode } from "@tiptap/pm/model"

import { FILL_BLANK_CHIP_CLASS } from "./fill-blank-html"
import { FillBlankChipNodeView } from "./fill-blank-chip-node-view"

export const FILL_BLANK_CHIP_NODE = "fillBlankChip"

export function countFillBlankChipsInDoc(doc: ProseMirrorNode): number {
  let count = 0
  doc.descendants((node) => {
    if (node.type.name === FILL_BLANK_CHIP_NODE) count++
  })
  return count
}

export function renumberFillBlankChipsInEditor(editor: Editor): void {
  const { state } = editor
  let index = 0
  let tr = state.tr
  let changed = false

  state.doc.descendants((node, pos) => {
    if (node.type.name !== FILL_BLANK_CHIP_NODE) return
    index++
    if (node.attrs.label !== index) {
      tr = tr.setNodeMarkup(pos, undefined, { label: index })
      changed = true
    }
  })

  if (changed) editor.view.dispatch(tr)
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    fillBlankChip: {
      insertFillBlankChip: () => ReturnType
    }
  }
}

export const FillBlankChipExtension = Node.create({
  name: FILL_BLANK_CHIP_NODE,
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      label: {
        default: 1,
        parseHTML: (element) => {
          const raw = element.getAttribute("data-fill-blank")
          const parsed = raw ? Number.parseInt(raw, 10) : 1
          return Number.isFinite(parsed) ? parsed : 1
        },
        renderHTML: (attributes) => ({
          "data-fill-blank": String(attributes.label),
          "aria-label": `Blank ${attributes.label}`,
          role: "img",
        }),
      },
    }
  },

  parseHTML() {
    return [{ tag: "span[data-fill-blank]" }]
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        contenteditable: "false",
        class: FILL_BLANK_CHIP_CLASS,
      }),
      `Blank ${node.attrs.label}`,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(FillBlankChipNodeView)
  },

  addCommands() {
    return {
      insertFillBlankChip:
        () =>
        ({ chain, state }) => {
          const label = countFillBlankChipsInDoc(state.doc) + 1
          return chain()
            .focus()
            .insertContent([
              { type: this.name, attrs: { label } },
              { type: "text", text: " " },
            ])
            .run()
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      "#": () => this.editor.commands.insertFillBlankChip(),
    }
  },
})
